<template>
  <div>
    <div class="dialog-mask" v-if="visible" @click.self="close">
      <div class="register-dialog">
        <!-- 注册表单 -->
        <template v-if="!showAvatarSelection">
          <div class="dialog-header">
            <h3>📝 用户注册</h3>
            <button class="close-btn" @click="close">×</button>
          </div>

          <form @submit.prevent="handleRegister">
            <div class="form-group">
              <label>用户名</label>
              <input type="text" v-model="form.username" placeholder="请输入用户名" required />
            </div>

            <div class="form-group">
              <label>邮箱</label>
              <input type="email" v-model="form.email" placeholder="请输入邮箱" required />
            </div>

            <div class="form-group">
              <label>密码</label>
              <input type="password" v-model="form.password" placeholder="请输入密码（至少6位）" required />
            </div>

            <div class="form-group">
              <label>确认密码</label>
              <input type="password" v-model="form.confirmPassword" placeholder="请再次输入密码" required />
            </div>

            <div class="form-group">
              <label>邀请码</label>
              <div class="input-with-hint">
                <input type="text" v-model="form.inviteCode" placeholder="请输入邀请码" required />
                <span class="hint-link" @click="showWechatQR = true">添加微信获取邀请码</span>
              </div>
            </div>

            <button type="submit" class="btn-register" :disabled="loading">
              {{ loading ? '注册中...' : '注 册' }}
            </button>
          </form>

          <div class="dialog-footer">
            <span>已有账号？</span>
            <a @click="goLogin">立即登录</a>
          </div>
        </template>

        <!-- 头像选择 -->
        <template v-else>
          <div class="dialog-header">
            <h3>🎉 注册成功</h3>
            <button class="close-btn" @click="close">×</button>
          </div>
          <p class="avatar-hint">请选择您的头像</p>
          <div class="avatar-grid">
            <div
              v-for="(avatar, index) in avatarOptions"
              :key="index"
              class="avatar-item"
              :class="{ selected: selectedAvatar === avatar }"
              @click="selectedAvatar = avatar"
            >
              <img :src="avatar" :alt="'头像' + (index + 1)" />
            </div>
          </div>
          <button class="btn-register" :disabled="!selectedAvatar || savingAvatar" @click="saveAvatar">
            {{ savingAvatar ? '保存中...' : '确认选择' }}
          </button>
          <button class="btn-skip" @click="skipAvatar">跳过，使用默认头像</button>
        </template>
      </div>
    </div>

    <!-- 微信二维码弹窗 -->
    <div class="wechat-modal" v-if="showWechatQR" @click="showWechatQR = false">
      <div class="wechat-modal-content" @click.stop>
        <span class="close-btn" @click="showWechatQR = false">&times;</span>
        <h3>添加微信获取邀请码</h3>
        <img src="@/img/wechat.jpg" alt="微信二维码" class="qr-img" />
        <p>扫码添加微信，获取邀请码</p>
      </div>
    </div>
  </div>
</template>

<script>
import { registerApi, selectAvatarApi } from '@/api/index'

export default {
  name: 'RegisterDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        inviteCode: ''
      },
      loading: false,
      showWechatQR: false,
      showAvatarSelection: false,
      avatarOptions: [],
      selectedAvatar: '',
      savingAvatar: false,
      registeredUserId: null,
      registeredUsername: ''
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.form = { username: '', email: '', password: '', confirmPassword: '', inviteCode: '' }
      this.showWechatQR = false
      this.showAvatarSelection = false
      this.avatarOptions = []
      this.selectedAvatar = ''
      this.registeredUserId = null
      this.registeredUsername = ''
    },
    normalizeAvatarOptions(options) {
      const defaults = [
        '/static/avatars/avatar1.svg',
        '/static/avatars/avatar2.svg',
        '/static/avatars/avatar3.svg',
        '/static/avatars/avatar4.svg',
        '/static/avatars/avatar5.svg',
        '/static/avatars/avatar6.svg'
      ]
      if (!options || options.length === 0) {
        return defaults
      }
      return options.map(url => (url && url.includes('dicebear.com')) ? '/static/avatars/avatar1.svg' : url)
    },
    close() {
      this.$emit('update:visible', false)
    },
    goLogin() {
      this.close()
      this.$emit('switch-login')
    },
    async handleRegister() {
      if (this.loading) return

      if (!this.form.username || !this.form.email || !this.form.password) {
        this.$toast.warning('请填写完整信息')
        return
      }

      if (this.form.password !== this.form.confirmPassword) {
        this.$toast.warning('两次输入的密码不一致')
        return
      }

      if (this.form.password.length < 6) {
        this.$toast.warning('密码长度不能少于6位')
        return
      }

      if (!this.form.inviteCode) {
        this.$toast.warning('请输入邀请码')
        return
      }

      try {
        this.loading = true
        const response = await registerApi({
          username: this.form.username,
          password: this.form.password,
          email: this.form.email,
          invite_code: this.form.inviteCode
        })

        if (response.code === 200) {
          this.registeredUserId = response.data.user_id
          this.registeredUsername = response.data.username
          this.avatarOptions = this.normalizeAvatarOptions(response.data.avatar_options)
          this.showAvatarSelection = true
        } else {
          this.$toast.error(response.message || '注册失败')
        }
      } catch (error) {
        console.error('Register error:', error)
        const errorMsg = error.response?.data?.message || error.message || '网络连接失败'
        this.$toast.error(errorMsg)
      } finally {
        this.loading = false
      }
    },

    async saveAvatar() {
      if (!this.selectedAvatar) {
        this.$toast.warning('请选择一个头像')
        return
      }
      this.savingAvatar = true
      try {
        const response = await selectAvatarApi({
          user_id: this.registeredUserId,
          avatar: this.selectedAvatar
        })
        if (response.code === 200) {
          this.$toast.success('注册成功！请登录')
          this.close()
          this.$emit('switch-login')
        } else {
          this.skipAvatar()
        }
      } catch (error) {
        this.skipAvatar()
      } finally {
        this.savingAvatar = false
      }
    },

    skipAvatar() {
      this.$toast.success('注册成功！请登录')
      this.close()
      this.$emit('switch-login')
    }
  }
}
</script>

<style scoped>
.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.register-dialog {
  background: white;
  border-radius: 12px;
  padding: 28px 32px;
  width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.dialog-header h3 {
  font-size: 18px;
  color: #1e293b;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #94a3b8;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #64748b;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.input-with-hint {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-with-hint input {
  flex: 1;
}

.hint-link {
  color: #0066ff;
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
}

.hint-link:hover {
  text-decoration: underline;
}

/* 微信二维码弹窗 */
.wechat-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.wechat-modal-content {
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  max-width: 320px;
  width: 90%;
}

.wechat-modal-content h3 {
  margin-bottom: 15px;
  color: #333;
}

.qr-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  border-radius: 8px;
}

.wechat-modal-content p {
  margin-top: 15px;
  color: #666;
  font-size: 0.9rem;
}

.btn-register {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 11px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  width: 100%;
  margin-top: 4px;
  transition: all 0.3s;
}

.btn-register:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-skip {
  background: transparent;
  color: #94a3b8;
  border: none;
  padding: 10px;
  cursor: pointer;
  font-size: 13px;
  width: 100%;
  margin-top: 8px;
  transition: color 0.2s;
}

.btn-skip:hover {
  color: #64748b;
}

.dialog-footer {
  margin-top: 16px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

.dialog-footer a {
  color: #0066ff;
  cursor: pointer;
  margin-left: 4px;
  font-weight: 500;
}

.dialog-footer a:hover {
  text-decoration: underline;
}

.avatar-hint {
  text-align: center;
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
}

.avatar-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 16px 0;
}

.avatar-item {
  aspect-ratio: 1;
  border: 3px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-item:hover {
  border-color: #0066ff;
  transform: scale(1.05);
}

.avatar-item.selected {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.2);
}

.avatar-item img {
  width: 75%;
  height: 75%;
  object-fit: contain;
}
</style>
